import { useState } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  message: string;
  type: ToastType;
  id: number;
}

/**
 * Custom hook for managing toast notifications
 */
export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * Show a toast notification
   * @param message - The message to display
   * @param type - The type of toast (success, error, warning, info)
   * @param duration - How long to show the toast (in ms), defaults to 5000ms
   */
  const showToast = (message: string, type: ToastType = 'info', duration: number = 5000) => {
    // Generate a unique ID using timestamp plus a random number
    const id = Date.now() + Math.floor(Math.random() * 10000);
    
    // Add the new toast to the array
    setToasts((prevToasts) => [...prevToasts, { message, type, id }]);
    
    // Remove the toast after the specified duration
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, duration);
    
    return id;
  };

  /**
   * Remove a specific toast by ID
   * @param id - The ID of the toast to remove
   */
  const removeToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  /**
   * Clear all toasts
   */
  const clearToasts = () => {
    setToasts([]);
  };

  return {
    toasts,
    showToast,
    removeToast,
    clearToasts
  };
};
